import { DealPageHeader } from "@/components/deal-page-header";
import { DealNav } from "@/components/deal-nav";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getDealDetail } from "@/lib/deals.functions";
import { getRaiseStrategyReportForDeal } from "@/lib/raise-strategy.functions";
import { useAuth } from "@/hooks/use-auth";

export const Route = createFileRoute(
  "/_authed/deals/$dealId_/raise-strategy/$reportId",
)({
  head: () => ({
    meta: [
      { title: "Raise Strategy Report: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: RaiseStrategyReportPage,
});

/* Defensive readers: report_json is model assembled. */
type Dict = Record<string, unknown>;

function asDict(v: unknown): Dict | null {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Dict) : null;
}
function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}
function asDicts(v: unknown): Dict[] {
  return asArray(v)
    .map(asDict)
    .filter((d): d is Dict => !!d);
}
function asStr(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v : null;
}
function asNum(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function strList(v: unknown): string[] {
  return asArray(v)
    .map(asStr)
    .filter((s): s is string => !!s);
}

function Section({
  index,
  title,
  children,
}: {
  index: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-brand-line bg-brand-paper p-5">
      <h2 className="font-serif text-lg text-brand-ink">
        {index}. {title}
      </h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Callout({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-4 rounded-lg border border-brand-accent/30 bg-brand-accent/5 p-4 text-sm text-brand-ink">
      {children}
    </div>
  );
}

function Table({
  headers,
  rows,
}: {
  headers: string[];
  rows: (string | number | null)[][];
}) {
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full min-w-[36rem] text-left text-sm">
        <thead>
          <tr className="border-b border-brand-line">
            {headers.map((h) => (
              <th key={h} className="py-2 pr-3 text-xs font-medium text-muted-foreground">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-brand-line/60 align-top">
              {r.map((c, j) => (
                <td key={j} className="py-2 pr-3 text-brand-ink">
                  {c ?? ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Paragraphs({ items }: { items: string[] }) {
  return (
    <>
      {items.map((p, i) => (
        <p key={i} className="mt-2 text-sm text-brand-ink">
          {p}
        </p>
      ))}
    </>
  );
}

function Phase({ phase, label }: { phase: Dict | null; label: string }) {
  if (!phase) return null;
  const actions = strList(phase["actions"]);
  const stops = strList(phase["stop_conditions"]);
  return (
    <div className="mt-4">
      <h3 className="text-sm font-medium text-brand-ink">
        {label}
        {asStr(phase["title"]) ? `: ${asStr(phase["title"])}` : ""}
      </h3>
      {asStr(phase["body"]) && (
        <p className="mt-1 text-sm text-brand-ink">{asStr(phase["body"])}</p>
      )}
      {actions.length > 0 && (
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-brand-ink">
          {actions.map((a, i) => (
            <li key={i}>{a}</li>
          ))}
        </ul>
      )}
      {stops.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-medium text-muted-foreground">
            Stop conditions
          </p>
          <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-brand-ink">
            {stops.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function TemplateBlock({
  label,
  subject,
  body,
}: {
  label: string;
  subject?: string | null;
  body: string;
}) {
  return (
    <div className="mt-3 rounded-lg border border-brand-line bg-brand-paper-2 p-4">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      {subject && (
        <p className="mt-2 text-sm text-brand-ink">
          <span className="font-medium">Subject: </span>
          {subject}
        </p>
      )}
      <p className="mt-2 whitespace-pre-wrap text-sm text-brand-ink">{body}</p>
    </div>
  );
}

function RaiseStrategyReportPage() {
  const { dealId, reportId } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fetchReport = useServerFn(getRaiseStrategyReportForDeal);
  const fetchDeal = useServerFn(getDealDetail);

  const reportQuery = useQuery({
    queryKey: ["raise-strategy-report", dealId, reportId],
    queryFn: () => fetchReport({ data: { dealId, reportId } }),
    enabled: !!user,
  });

  const dealQuery = useQuery({
    queryKey: ["deal-detail", dealId],
    queryFn: () => fetchDeal({ data: { dealId } }),
    enabled: !!user,
  });

  const row = (reportQuery.data?.report ?? null) as Dict | null;
  const status = asStr(row?.["report_status"]);

  useEffect(() => {
    if (status === "processing") {
      navigate({ to: "/deals/$dealId", params: { dealId } });
    }
  }, [status, dealId, navigate]);

  useEffect(() => {
    document.body.classList.add("model-review-print");
    return () => {
      document.body.classList.remove("model-review-print");
    };
  }, []);

  if (reportQuery.isLoading || dealQuery.isLoading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10 text-sm text-muted-foreground">
        Loading the report...
      </div>
    );
  }

  if (reportQuery.isError || !row) {
    return (
      <div className="mx-auto max-w-4xl space-y-3 px-4 py-10">
        <p className="text-sm text-brand-ink">This report is not available.</p>
        <Link
          to="/deals/$dealId"
          params={{ dealId }}
          className="text-sm underline underline-offset-2"
        >
          Back to the deal
        </Link>
      </div>
    );
  }

  const dealName =
    asStr((dealQuery.data as { deal?: { name?: string } } | undefined)?.deal?.name) ??
    "Deal";
  const pageTitle = `${dealName} Raise Strategy Report`;

  const handleDownloadPdf = () => {
    const originalTitle = document.title;
    document.title = pageTitle;
    const restore = () => {
      document.title = originalTitle;
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    setTimeout(() => {
      if (document.title === pageTitle) {
        document.title = originalTitle;
        window.removeEventListener("afterprint", restore);
      }
    }, 1000);
    window.print();
  };

  if (status === "failed") {
    return (
      <div className="mx-auto max-w-4xl space-y-3 px-4 py-10">
        <h1 className="font-serif text-2xl text-brand-ink">{pageTitle}</h1>
        <p className="text-sm text-brand-ink">
          This report did not finish. Any credits charged were returned.
        </p>
        <Link
          to="/deals/$dealId"
          params={{ dealId }}
          className="text-sm underline underline-offset-2"
        >
          Back to the deal
        </Link>
      </div>
    );
  }

  if (status === "processing") return null;

  const report = asDict(row["report_json"]) ?? {};
  const header = asDict(report["header"]) ?? {};
  const s = asDict(report["sections"]) ?? {};
  const disclaimer = asStr(report["disclaimer"]);

  /* 1 */
  const stands = asDict(s["where_your_raise_stands"]) ?? {};
  const statusCounts = asDicts(stands["status_counts"]);

  /* 2 */
  const pool = asDict(s["your_investor_pool"]) ?? {};
  const composition = asDicts(pool["composition_table"]);
  const typeMeanings = asDicts(pool["type_meanings"]);
  const meaningFor = (t: string | null) =>
    asStr(
      typeMeanings.find(
        (m) => (asStr(m["investor_type"]) ?? "").toLowerCase() === (t ?? "").toLowerCase(),
      )?.["what_it_means_for_approach"],
    ) ?? "";
  const geo = asDict(pool["geography"]) ?? {};

  /* 3 */
  const process = asDict(s["process_plan"]) ?? {};

  /* 4 */
  const channels = asDicts(s["channel_strategy"]);

  /* 5 */
  const positioning = asDicts(s["positioning_by_investor_type"]);

  /* 6 */
  const sendOrder = asDict(s["your_send_order"]) ?? {};
  const topNotes = asDicts(sendOrder["top_contact_notes"]);
  const sendOrderClosing = asStr(sendOrder["closing_line"]);

  /* 7 */
  const passes = asDict(s["handling_passes"]) ?? {};
  const sampleEmail = asDict(passes["sample_email"]);

  /* 8 */
  const lead = asDict(s["lead_investor_path"]) ?? {};
  const leadTitle =
    asStr(lead["section_title"]) ?? asStr(lead["title"]) ?? "Lead Investor Path";

  /* 9 */
  const beyond = asDict(s["beyond_direct_outreach"]) ?? {};
  const conferences = asDict(beyond["conferences"]);
  const communities = asDict(beyond["communities"]);
  const demoDays = asDict(beyond["demo_days"]);
  const additional = asDicts(beyond["additional_ideas"]);

  /* 10 */
  const weekly = asDicts(s["weekly_working_plan"]);

  // Empty-card guards: these sections have fully-guarded children, so the
  // card (heading included) must not render when nothing inside would.
  const hasStands =
    !!asStr(stands["stage_read"]) ||
    strList(stands["paragraphs"]).length > 0 ||
    !!asStr(stands["core_principle"]) ||
    statusCounts.length > 0;
  const hasSendOrder =
    topNotes.length > 0 || !!sendOrderClosing || !!asStr(sendOrder["callout"]);
  const hasPasses =
    !!asStr(passes["policy"]) ||
    !!(sampleEmail && asStr(sampleEmail["body"])) ||
    !!asStr(passes["sample_linkedin"]);
  const hasLead =
    !!asStr(lead["credible_lead_profile"]) ||
    !!asStr(lead["fitting_contacts_estimate"]) ||
    strList(lead["readiness_signals"]).length > 0 ||
    !!asStr(lead["reading_the_stop_conditions"]);
  const hasBeyond =
    strList(beyond["linkedin_content_topics"]).length > 0 ||
    !!asStr(beyond["referral_ask"]) ||
    !!(conferences && asDicts(conferences["items"]).length > 0) ||
    !!(communities && asDicts(communities["items"]).length > 0) ||
    !!asStr(beyond["university_and_alumni"]) ||
    !!(demoDays && demoDays["applicable"] === true && asStr(demoDays["body"])) ||
    additional.length > 0;

  const geoBlock = (label: string, rows: Dict[]) =>
    rows.length === 0 ? null : (
      <p key={label} className="mt-2 text-sm text-brand-ink">
        <span className="font-medium">{label}: </span>
        {rows
          .map(
            (r) =>
              `${asStr(r["label"]) ?? "Unknown"} ${asNum(r["contacts"]) ?? 0} (${asNum(r["share_pct"]) ?? 0}%)`,
          )
          .join(", ")}
      </p>
    );

  return (
    <div className="model-review-report mx-auto max-w-4xl space-y-5 px-4 py-8">
      <style>{`
        @media print {
          @page { margin: 0.75in; }
          body.model-review-print {
            background: #fff !important;
            color: #000 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          body.model-review-print header.sticky { display: none !important; }
          .model-review-report .no-print { display: none !important; }
          .model-review-report {
            max-width: none !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .model-review-report section,
          .model-review-report .rounded-lg {
            border: none !important;
            background: transparent !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            padding: 0.5rem 0 !important;
            margin: 1rem 0 !important;
          }
          .model-review-report h1,
          .model-review-report h2,
          .model-review-report h3 {
            color: #000 !important;
            break-after: avoid;
          }
          .model-review-report .font-serif { color: #000 !important; }
          .model-review-report p,
          .model-review-report li,
          .model-review-report td,
          .model-review-report th { color: #000 !important; }
          .model-review-report .text-muted-foreground,
          .model-review-report .text-brand-muted { color: #333 !important; }
          .model-review-report .overflow-x-auto {
            overflow: visible !important;
            width: 100% !important;
          }
          .model-review-report table {
            width: 100% !important;
            min-width: 0 !important;
            border-collapse: collapse !important;
            page-break-inside: auto !important;
          }
          .model-review-report thead { display: table-row-group !important; }
          .model-review-report tr {
            border-top: 1px solid #ccc !important;
            border-bottom: 1px solid #ccc !important;
            page-break-inside: auto !important;
          }
          .model-review-report td,
          .model-review-report th {
            padding: 0.35rem 0.5rem 0.35rem 0 !important;
            vertical-align: top !important;
            border: none !important;
          }
          .model-review-report .border-brand-line { border-color: #ccc !important; }
        }
      `}</style>

      <div className="no-print space-y-6">
        <DealPageHeader dealId={dealId} />
        <DealNav dealId={dealId} active="reports" />
      </div>

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl text-brand-ink">{pageTitle}</h1>
          {asStr(header["raise_summary_line"]) && (
            <p className="mt-1 text-sm text-brand-muted">
              {asStr(header["raise_summary_line"])}
            </p>
          )}
          <p className="mt-1 text-xs text-muted-foreground">
            {asStr(header["prepared_date"])
              ? `Prepared ${new Date(String(header["prepared_date"])).toLocaleDateString()}`
              : ""}
            {asNum(header["contact_count"]) != null
              ? ` · ${asNum(header["contact_count"])} contacts`
              : ""}
          </p>
        </div>
        <div className="flex items-center gap-3 no-print">
          <button
            type="button"
            onClick={handleDownloadPdf}
            className="shrink-0 text-sm underline underline-offset-2"
          >
            Download PDF
          </button>
          <Link
            to="/deals/$dealId"
            params={{ dealId }}
            className="shrink-0 text-sm underline underline-offset-2"
          >
            Back to the deal
          </Link>
        </div>
      </div>

      {/* 1 */}
      {hasStands && (
      <Section index={1} title="Where Your Raise Stands">
        {asStr(stands["stage_read"]) && (
          <p className="text-sm font-medium text-brand-ink">
            {asStr(stands["stage_read"])}
          </p>
        )}
        <Paragraphs items={strList(stands["paragraphs"])} />
        {asStr(stands["core_principle"]) && (
          <Callout>{asStr(stands["core_principle"])}</Callout>
        )}
        {statusCounts.length > 0 && (
          <p className="mt-3 text-sm text-brand-ink">
            <span className="font-medium">Contact statuses: </span>
            {statusCounts
              .map(
                (c) =>
                  `${asStr(c["investor_status"]) ?? "Unknown"} ${asNum(c["contacts"]) ?? 0}`,
              )
              .join(", ")}
          </p>
        )}
      </Section>
      )}

      {/* 2 */}
      <Section index={2} title="Your Investor Pool">
        <p className="text-sm text-brand-ink">
          {asNum(pool["total_contacts"]) ?? 0} contacts across{" "}
          {asNum(pool["total_firms"]) ?? 0} firms.
        </p>
        {composition.length > 0 && (
          <Table
            headers={[
              "Investor Type",
              "Contacts",
              "Share",
              "What This Type Means for Your Approach",
            ]}
            rows={composition.map((c) => [
              asStr(c["investor_type"]) ?? "Unknown",
              asNum(c["contacts"]) ?? 0,
              `${asNum(c["contact_share_pct"]) ?? 0}%`,
              meaningFor(asStr(c["investor_type"])),
            ])}
          />
        )}
        {asStr(pool["geography_summary"]) && (
          <p className="mt-4 text-sm text-brand-ink">
            {asStr(pool["geography_summary"])}
          </p>
        )}
        {geoBlock("Cities", asDicts(geo["by_city"]).slice(0, 10))}
        {geoBlock("States", asDicts(geo["by_state"]).slice(0, 10))}
        {geoBlock("Countries", asDicts(geo["by_country"]).slice(0, 10))}
        {asStr(pool["strategic_callout"]) && (
          <Callout>{asStr(pool["strategic_callout"])}</Callout>
        )}
      </Section>

      {/* 3 */}
      <Section index={3} title="Process Plan">
        <p className="text-sm text-brand-muted">
          {asNum(process["send_rate_per_day"]) ?? 0} contacts per day,{" "}
          {asNum(process["business_days_to_send_whole_list"]) ?? 0} business days
          to reach the whole list.
        </p>
        <Phase phase={asDict(process["phase_1"])} label="Phase 1" />
        <Phase phase={asDict(process["phase_2"])} label="Phase 2" />
        <Phase phase={asDict(process["phase_3"])} label="Phase 3" />
      </Section>

      {/* 4 */}
      {channels.length > 0 && (
        <Section index={4} title="Channel Strategy">
          <Table
            headers={["Channel", "Role", "Who", "Timing", "Rules"]}
            rows={channels.map((c) => [
              asStr(c["channel"]),
              asStr(c["role"]),
              asStr(c["who"]),
              asStr(c["timing"]),
              asStr(c["rules"]),
            ])}
          />
        </Section>
      )}

      {/* 5 */}
      {positioning.length > 0 && (
        <Section index={5} title="Positioning by Investor Type">
          <Table
            headers={["Investor Type", "Lead With", "Expect Pushback On"]}
            rows={positioning.map((p) => [
              asStr(p["investor_type"]),
              asStr(p["lead_with"]),
              asStr(p["expect_pushback_on"]),
            ])}
          />
        </Section>
      )}

      {/* 6 */}
      {hasSendOrder && (
      <Section index={6} title="Your Send Order">
        {topNotes.length > 0 && (
          <div className="divide-y divide-brand-line">
            {topNotes.map((n, i) => (
              <div key={i} className="py-3 first:pt-0">
                <p className="text-sm font-medium text-brand-ink">
                  {asNum(n["rank"]) ?? i + 1}. {asStr(n["name"]) ?? "Unnamed contact"}
                  {asStr(n["firm"]) ? `, ${asStr(n["firm"])}` : ""}
                </p>
                {asStr(n["why_early"]) && (
                  <p className="mt-1 text-sm text-brand-ink">
                    {asStr(n["why_early"])}
                  </p>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                  Channel: {asStr(n["channel"]) ?? "Email"}
                  {n["founder_direct"] === true ? " · Founder direct" : ""}
                </p>
              </div>
            ))}
          </div>
        )}

        {sendOrderClosing && (
          <p className="mt-3 text-sm text-brand-ink">{sendOrderClosing}</p>
        )}

        {asStr(sendOrder["callout"]) && (
          <Callout>{asStr(sendOrder["callout"])}</Callout>
        )}
      </Section>
      )}

      {/* 7 */}
      {hasPasses && (
      <Section index={7} title="Handling Passes">
        {asStr(passes["policy"]) && (
          <p className="text-sm text-brand-ink">{asStr(passes["policy"])}</p>
        )}
        {sampleEmail && asStr(sampleEmail["body"]) && (
          <TemplateBlock
            label="Sample email"
            subject={asStr(sampleEmail["subject"])}
            body={String(sampleEmail["body"])}
          />
        )}
        {asStr(passes["sample_linkedin"]) && (
          <TemplateBlock
            label="Sample LinkedIn message"
            body={String(passes["sample_linkedin"])}
          />
        )}
      </Section>
      )}

      {/* 8 */}
      {hasLead && (
      <Section index={8} title={leadTitle}>
        {asStr(lead["credible_lead_profile"]) && (
          <p className="text-sm text-brand-ink">
            {asStr(lead["credible_lead_profile"])}
          </p>
        )}
        {asStr(lead["fitting_contacts_estimate"]) && (
          <p className="mt-2 text-sm text-brand-ink">
            {asStr(lead["fitting_contacts_estimate"])}
          </p>
        )}
        {strList(lead["readiness_signals"]).length > 0 && (
          <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-brand-ink">
            {strList(lead["readiness_signals"]).map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ol>
        )}
        {asStr(lead["reading_the_stop_conditions"]) && (
          <p className="mt-3 text-sm text-brand-ink">
            {asStr(lead["reading_the_stop_conditions"])}
          </p>
        )}
      </Section>
      )}

      {/* 9 */}
      {hasBeyond && (
      <Section index={9} title="Beyond Direct Outreach">
        {strList(beyond["linkedin_content_topics"]).length > 0 && (
          <>
            <h3 className="text-sm font-medium text-brand-ink">
              LinkedIn content topics
            </h3>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-brand-ink">
              {strList(beyond["linkedin_content_topics"]).map((t, i) => (
                <li key={i}>{t}</li>
              ))}
            </ul>
          </>
        )}

        {asStr(beyond["referral_ask"]) && (
          <>
            <h3 className="mt-4 text-sm font-medium text-brand-ink">
              Founder referrals
            </h3>
            <p className="mt-1 text-sm text-brand-ink">
              {asStr(beyond["referral_ask"])}
            </p>
          </>
        )}

        {conferences && asDicts(conferences["items"]).length > 0 && (
          <>
            <h3 className="mt-4 text-sm font-medium text-brand-ink">Conferences</h3>
            {conferences["verified"] === false && asStr(conferences["note"]) && (
              <p className="mt-1 text-xs text-muted-foreground">
                {asStr(conferences["note"])}
              </p>
            )}
            <Table
              headers={["Name", "Dates", "Location", "Why Relevant"]}
              rows={asDicts(conferences["items"]).map((c) => [
                asStr(c["name"]),
                asStr(c["dates"]),
                asStr(c["location"]),
                asStr(c["why_relevant"]),
              ])}
            />
          </>
        )}

        {communities && asDicts(communities["items"]).length > 0 && (
          <>
            <h3 className="mt-4 text-sm font-medium text-brand-ink">Communities</h3>
            {communities["verified"] === false && asStr(communities["note"]) && (
              <p className="mt-1 text-xs text-muted-foreground">
                {asStr(communities["note"])}
              </p>
            )}
            <ul className="mt-2 space-y-2 text-sm text-brand-ink">
              {asDicts(communities["items"]).map((c, i) => (
                <li key={i}>
                  <span className="font-medium">{asStr(c["name"])}</span>
                  {asStr(c["why_relevant"]) ? `: ${asStr(c["why_relevant"])}` : ""}
                  {asStr(c["how_to_join"]) && (
                    <span className="block text-xs text-muted-foreground">
                      How to join: {asStr(c["how_to_join"])}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}

        {asStr(beyond["university_and_alumni"]) && (
          <>
            <h3 className="mt-4 text-sm font-medium text-brand-ink">
              University and alumni networks
            </h3>
            <p className="mt-1 text-sm text-brand-ink">
              {asStr(beyond["university_and_alumni"])}
            </p>
          </>
        )}

        {demoDays && demoDays["applicable"] === true && asStr(demoDays["body"]) && (
          <>
            <h3 className="mt-4 text-sm font-medium text-brand-ink">Demo days</h3>
            <p className="mt-1 text-sm text-brand-ink">{asStr(demoDays["body"])}</p>
          </>
        )}

        {additional.length > 0 && (
          <>
            <h3 className="mt-4 text-sm font-medium text-brand-ink">
              Additional ideas
            </h3>
            <ul className="mt-2 space-y-2 text-sm text-brand-ink">
              {additional.map((a, i) => (
                <li key={i}>
                  <span className="font-medium">{asStr(a["title"])}</span>
                  {asStr(a["body"]) ? `: ${asStr(a["body"])}` : ""}
                </li>
              ))}
            </ul>
          </>
        )}
      </Section>
      )}

      {/* 10 */}
      {weekly.length > 0 && (
        <Section index={10} title="Weekly Working Plan">
          <Table
            headers={["Week", "Send Block", "Channels", "In Parallel", "Output"]}
            rows={weekly.map((w) => [
              asStr(w["week"]),
              asStr(w["send_block"]),
              asStr(w["channels"]),
              asStr(w["in_parallel"]),
              asStr(w["output"]),
            ])}
          />
        </Section>
      )}

      {disclaimer && (
        <p className="text-xs text-muted-foreground">{disclaimer}</p>
      )}
    </div>
  );
}
